// Shared detection + one-shot reload guard for post-deploy code-split chunk failures.
//
// Why this exists: a new deploy re-hashes only the chunks whose source changed. A browser tab
// still running the PREVIOUS build (or hitting an edge POP that hasn't finished propagating the
// new upload) then lazy-loads a component and requests a chunk URL that no longer exists → the
// browser throws "ChunkLoadError". A single hard reload pulls the current HTML + manifest +
// chunks and resolves it. Retrying the SAME import URL does not help (the file is genuinely a
// different hash now), so a full reload — not an import retry — is the correct remedy.
//
// The guard reloads at most once per window so that a genuinely-broken deploy (chunk truly
// missing) surfaces the error boundary instead of reloading forever.

const GUARD_KEY = "chunkReloadAt";
const GUARD_WINDOW_MS = 15000;

export function isChunkLoadError(value: unknown): boolean {
  if (!value) return false;
  const err = value as { name?: string; message?: string };
  const name = typeof err.name === "string" ? err.name : "";
  const msg = typeof err.message === "string" ? err.message : String(value);
  return (
    name === "ChunkLoadError" ||
    /Loading chunk [\w./-]+ failed/i.test(msg) ||
    /Failed to load chunk/i.test(msg) ||
    /(Failed to fetch|error loading) dynamically imported module/i.test(msg)
  );
}

// Reload at most once per GUARD_WINDOW_MS. Returns true if a reload was triggered.
export function reloadOnceForChunkError(): boolean {
  if (typeof window === "undefined") return false;
  let last = 0;
  try {
    last = Number(window.sessionStorage.getItem(GUARD_KEY) || "0");
  } catch {
    // sessionStorage may throw (privacy mode / disabled) — fall through and reload unguarded.
  }
  const now = Date.now();
  if (last && now - last < GUARD_WINDOW_MS) return false; // just tried — the chunk is really gone
  try {
    window.sessionStorage.setItem(GUARD_KEY, String(now));
  } catch {
    // ignore
  }
  window.location.reload();
  return true;
}
